import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Wind, Sun, Battery, Zap } from 'lucide-react';

export const Hero = ({ onConsultationClick }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden pt-0">
      {/* Industrial Energy Background Elements */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-20 right-10">
          <Wind size={120} className="text-slate-400" />
        </div>
        <div className="absolute bottom-32 left-16">
          <Sun size={80} className="text-slate-400" />
        </div>
        <div className="absolute top-1/3 right-1/4">
          <Battery size={60} className="text-slate-400" />
        </div>
        <div className="absolute bottom-20 right-20">
          <Zap size={100} className="text-slate-400" />
        </div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pt-4 pb-0">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left Content - Logo + Main Image */}
          <div className="lg:col-span-5 flex flex-col h-full">
            {/* Large Ecological Logo - aligned with tagline bottom */}
            <div className="mb-8 -mt-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_clean-energy/artifacts/1r5qyu8d_Ecological2020%20-no-shadow.png"
                alt="Ecological Energy" 
                className="w-full h-auto max-w-md"
              />
            </div>
            
            {/* Main Image - extends to align with button */}
            <div className="relative flex-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_clean-energy/artifacts/jhox17yo_EWT_Fleetwood.jpg"
                alt="Commercial Solar and Wind Energy Installation" 
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:col-span-7">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-slate-900 leading-tight mb-4 tracking-tighter">
              Transition matters
            </h1>
            <p className="text-xl md:text-2xl font-medium text-green-600 mb-8">
              &nbsp;Energy should be ethical, practical and profitable.
            </p>
            
            <p className="text-xl text-slate-600 font-light leading-relaxed mb-12 max-w-2xl">
              Ecological Energy is a UK-based consultancy specialising in onshore wind, commercial solar, energy storage and power infrastructure.
            </p>

            {/* Achievement Stats - Visual Blocks */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              {/* Wind Projects Block */}
              <div className="relative overflow-hidden rounded-2xl p-6 group hover:shadow-lg transition-all duration-300">
                {/* EWT Turbine background image */}
                <div className="absolute inset-0 opacity-30">
                  <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_clean-energy/artifacts/bq9u6t3n_EWT_Turbine.jpg')`
                    }}
                  ></div>
                </div>
                
                {/* Wind turbine icon overlay */}
                <div className="absolute inset-0 opacity-15">
                  <div className="absolute top-4 right-4">
                    <Wind size={60} className="text-slate-600" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="text-3xl font-bold text-black mb-2">40+</div>
                  <div className="text-black font-medium leading-snug">
                    Onshore wind projects delivered
                  </div>
                  <div className="mt-3 w-12 h-1 bg-green-600 rounded-full"></div>
                </div>
              </div>

              {/* Solar Projects Block */}
              <div className="relative overflow-hidden rounded-2xl p-6 group hover:shadow-lg transition-all duration-300">
                {/* Solar carport background image */}
                <div className="absolute inset-0 opacity-40">
                  <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_clean-energy/artifacts/7b4ctlg0_carport.jpg')`
                    }}
                  ></div>
                </div>
                
                {/* Solar icon overlay */}
                <div className="absolute inset-0 opacity-15">
                  <div className="absolute top-4 right-4">
                    <Sun size={60} className="text-orange-500" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="text-3xl font-bold text-black mb-2">50+</div>
                  <div className="text-black font-medium leading-snug">
                    Commercial solar sites developed
                  </div>
                  <div className="mt-3 w-12 h-1 bg-orange-500 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 mb-0">
              <Button 
                onClick={() => window.open('https://calendly.com/iain_beveridge/30-minute-demo', '_blank')}
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-base font-medium transition-colors border-0"
              >
                Book a call to discuss your project
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};